/**
 * Typed API client with credential handling, request timeout,
 * and retry logic with exponential backoff for GET requests.
 */

export interface RequestOptions {
  /** Request timeout in ms (default: 30000) */
  timeout?: number;
  /** Skip 401 interception (used for auth endpoints) */
  skipAuthIntercept?: boolean;
  /** Custom headers */
  headers?: Record<string, string>;
  /** AbortSignal for external cancellation */
  signal?: AbortSignal;
}

export interface RetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries: number;
  /** Base delay in ms for exponential backoff (default: 1000) */
  baseDelayMs: number;
  /** Maximum delay in ms (default: 4000) */
  maxDelayMs: number;
  /** HTTP status codes that trigger a retry (default: [408, 429, 500, 502, 503, 504]) */
  retryableStatuses: number[];
}

export interface ApiErrorBody {
  error: string
  message: string
  timestamp: string
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body?: ApiErrorBody,
  ) {
    let errorMsg: string
    if (body?.message) {
      errorMsg = body.message
    } else if (body?.error) {
      errorMsg = body.error
    } else {
      errorMsg = statusText
    }
    super(`HTTP ${status}: ${errorMsg}`);
    this.name = 'ApiError';
  }
}

const DEFAULT_TIMEOUT_MS = 30_000;

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 4000,
  retryableStatuses: [408, 429, 500, 502, 503, 504],
};

/**
 * Computes the retry delay using exponential backoff.
 * Formula: min(baseDelayMs * 2^attempt, maxDelayMs)
 */
export function computeRetryDelay(attempt: number, config: RetryConfig): number {
  return Math.min(config.baseDelayMs * Math.pow(2, attempt), config.maxDelayMs);
}

/**
 * Determines whether a given HTTP status code is retryable.
 */
function isRetryableStatus(status: number, config: RetryConfig): boolean {
  return config.retryableStatuses.includes(status);
}

/**
 * Sleeps for the specified duration in milliseconds.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Executes a fetch request with timeout support via AbortController.
 */
async function fetchWithTimeout(
  input: RequestInfo,
  init: RequestInit,
  timeoutMs: number,
  externalSignal?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // If an external signal is provided, abort when it fires
  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', onExternalAbort);
    }
  }

  try {
    const response = await fetch(input, {
      ...init,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) {
      externalSignal.removeEventListener('abort', onExternalAbort);
    }
  }
}

/**
 * Parses the response body. Returns the parsed JSON if the content type
 * is JSON, otherwise returns undefined for empty responses.
 */
async function parseResponseBody<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return (await response.json()) as T;
  }
  // For 204 No Content or non-JSON responses
  if (response.status === 204) {
    return undefined as T;
  }
  // Attempt JSON parse; return undefined if it fails
  const text = await response.text();
  if (text) {
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }
  return undefined as T;
}

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, options?: RequestOptions): Promise<T>;
}

/**
 * Creates the API client instance.
 */
export function createApiClient(
  baseUrl: string = '',
  retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG,
): ApiClient {
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const url = `${baseUrl}${path}`;
    const timeoutMs = options.timeout ?? DEFAULT_TIMEOUT_MS;

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...options.headers,
    };

    // Only set Content-Type for methods that send a body
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const init: RequestInit = {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    };

    const isGet = method === 'GET';
    const maxAttempts = isGet ? retryConfig.maxRetries + 1 : 1;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetchWithTimeout(url, init, timeoutMs, options.signal);

        if (response.ok) {
          return await parseResponseBody<T>(response);
        }

        // Check if we should retry (only GET requests on retryable statuses)
        if (isGet && attempt < maxAttempts - 1 && isRetryableStatus(response.status, retryConfig)) {
          const delay = computeRetryDelay(attempt, retryConfig);
          await sleep(delay);
          continue;
        }

        // Non-retryable error or final attempt — throw
        const errorBody = await parseResponseBody<ApiErrorBody>(response);
        throw new ApiError(response.status, response.statusText, errorBody);
      } catch (error) {
        // Re-throw ApiError as-is
        if (error instanceof ApiError) {
          throw error;
        }

        // Handle abort/timeout errors
        if (error instanceof DOMException && error.name === 'AbortError') {
          // If external signal was aborted, don't retry
          if (options.signal?.aborted) {
            throw new ApiError(0, 'Request aborted');
          }
          // Timeout — treat as retryable for GET requests
          if (isGet && attempt < maxAttempts - 1) {
            const delay = computeRetryDelay(attempt, retryConfig);
            await sleep(delay);
            continue;
          }
          throw new ApiError(0, 'Request timeout');
        }

        // Network errors — treat as retryable for GET requests
        if (isGet && attempt < maxAttempts - 1) {
          const delay = computeRetryDelay(attempt, retryConfig);
          await sleep(delay);
          continue;
        }

        throw new ApiError(0, 'Network error');
      }
    }

    // This should never be reached, but TypeScript needs it
    throw new ApiError(0, 'Unexpected error');
  }

  return {
    get<T>(path: string, options?: RequestOptions): Promise<T> {
      return request<T>('GET', path, undefined, options);
    },
    post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>('POST', path, body, options);
    },
    patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>('PATCH', path, body, options);
    },
    put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>('PUT', path, body, options);
    },
    delete<T>(path: string, options?: RequestOptions): Promise<T> {
      return request<T>('DELETE', path, undefined, options);
    },
  };
}

/** Default API client instance */
export const apiClient = createApiClient(import.meta.env.VITE_API_BASE_URL || '');
